'use client';

import { useCallback, useState } from 'react';
import type { PrepareAssetUploadInput, UploadProgress } from '@impact/assets';
import { useUploadAsset } from '@/lib/queries/assets';

type UploadAssetFileInput = Omit<PrepareAssetUploadInput, 'file'> & {
  file: File;
  signal?: AbortSignal;
  onProgress?: (progress: UploadProgress) => void;
};

export function useAssetUpload(defaults: Partial<Omit<PrepareAssetUploadInput, 'file'>> = {}) {
  const uploadMutation = useUploadAsset();
  const [progress, setProgress] = useState<UploadProgress | null>(null);

  const upload = useCallback(
    async (input: UploadAssetFileInput) => {
      setProgress({ loadedBytes: 0, totalBytes: input.file.size, percent: 0 });

      const result = await uploadMutation.mutateAsync({
        prepare: {
          ...defaults,
          ...input,
          file: input.file,
        },
        signal: input.signal,
        onProgress: (next) => {
          setProgress(next);
          input.onProgress?.(next);
        },
      });

      return result;
    },
    [defaults, uploadMutation],
  );

  return {
    upload,
    progress,
    isUploading: uploadMutation.isPending,
    isError: uploadMutation.isError,
    error: uploadMutation.error,
    data: uploadMutation.data,
    reset: () => {
      setProgress(null);
      uploadMutation.reset();
    },
  };
}
